import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Stack,
  CircularProgress,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

interface ReportControlsProps {
  weeks: number;
  selectedCategory: string;
  reportType: string;
  sortBy: string;
  categories: string[];
  loading: boolean;
  onWeeksChange: (weeks: number) => void;
  onCategoryChange: (category: string) => void;
  onReportTypeChange: (reportType: string) => void;
  onSortByChange: (sortBy: string) => void;
  onRefresh: () => void;
}

const ReportControls: React.FC<ReportControlsProps> = ({
  weeks,
  selectedCategory,
  reportType,
  sortBy,
  categories,
  loading,
  onWeeksChange,
  onCategoryChange,
  onReportTypeChange,
  onSortByChange,
  onRefresh,
}) => {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Report Settings
        </Typography>
        
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center">
          <TextField
            label="Number of Weeks"
            type="number"
            value={weeks}
            onChange={(e) => onWeeksChange(Math.max(1, parseInt(e.target.value) || 1))}
            inputProps={{ min: 1, max: 52 }}
            sx={{ minWidth: 150 }}
            data-testid="weeks-input"
          />
          
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel id="category-label">Category</InputLabel>
            <Select
              labelId="category-label"
              value={selectedCategory}
              label="Category"
              onChange={(e) => onCategoryChange(e.target.value)}
            >
              <MenuItem value="All Categories">All Categories</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel id="reporttype-label">Report Type</InputLabel>
            <Select
              labelId="reporttype-label"
              value={reportType}
              label="Report Type"
              onChange={(e) => onReportTypeChange(e.target.value)}
            >
              <MenuItem value="default">Default</MenuItem>
              <MenuItem value="by_category">By Category</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel id="sortby-label">Sort By</InputLabel>
            <Select
              labelId="sortby-label"
              value={sortBy}
              label="Sort By"
              onChange={(e) => onSortByChange(e.target.value)}
              disabled={loading}
            >
              <MenuItem value="progress_desc">Progress (High to Low)</MenuItem>
              <MenuItem value="progress_asc">Progress (Low to High)</MenuItem>
              <MenuItem value="alphabetical">Alphabetical</MenuItem>
            </Select>
          </FormControl>
          
          <Button
            variant="contained"
            onClick={onRefresh}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ReportControls;
